import type { BadgeVariant } from '../../components/ui/Badge';
import type { KhoaDich } from '../../i18n';
import type { LoiDatLaiMatKhau, LoiTen } from './nghiep-vu/thao-tac';
import type { UserStatus } from './types';

/**
 * Luật màu trạng thái người dùng — nhóm biến thể **trạng thái** của `Badge`:
 *
 * - ACTIVE xanh: vào được, đang dùng.
 * - INVITED xanh nhấn: đang trong tiến trình — chờ người được mời đặt mật khẩu.
 *   Không hổ phách: chờ là bình thường, chưa phải việc quản trị cần để mắt.
 * - DISABLED xám: quản trị chủ động khoá, không phải sự cố, nên không đỏ.
 */
export const TRANG_THAI_NGUOI_DUNG: Record<
  UserStatus,
  { khoa: KhoaDich; variant: Extract<BadgeVariant, 'neutral' | 'primary' | 'success' | 'warning' | 'danger'> }
> = {
  ACTIVE: { khoa: 'nguoiDung.trangThai.ACTIVE', variant: 'success' },
  INVITED: { khoa: 'nguoiDung.trangThai.INVITED', variant: 'primary' },
  DISABLED: { khoa: 'nguoiDung.trangThai.DISABLED', variant: 'neutral' },
};

/**
 * Nhãn Quản trị tổ chức (role lớp 1) ở cột "Vai trò" — nhóm biến thể **phân
 * loại** của `Badge`: quản trị là *loại* người dùng, không phải nặng hay nhẹ.
 * Người dùng thường không có nhãn lớp 1: ô đó liệt kê vai trò lớp 2 được gán,
 * để "vai trò" trên màn chỉ còn một nghĩa.
 */
export const BADGE_QUAN_TRI_TO_CHUC: {
  khoa: KhoaDich;
  variant: Extract<BadgeVariant, 'violet' | 'teal' | 'orange' | 'info'>;
} = { khoa: 'vaiTro.quanTriToChuc', variant: 'violet' };

/** Số dòng mỗi lần tải (`limit`). Tenant khoảng 30 người (ADR-0013), nên phần lớn chỉ cần một trang. */
export const MOT_TRANG = 20;

/** Lỗi tên hiển thị phía giao diện → khoá dịch; dùng chung cho hộp mời và hộp sửa. */
export const KHOA_LOI_TEN: Record<LoiTen, KhoaDich> = {
  trong: 'nguoiDung.fTenTrong',
  dai: 'nguoiDung.fQuaDai',
};

/**
 * Xác thực hai lớp của một người — nhóm biến thể **trạng thái** (`primary`):
 * "Bắt buộc" là một chính sách đang áp, không tốt không xấu, nên không xanh lá
 * (đọc thành "an toàn") và không đỏ (đọc thành "có sự cố").
 */
export const BADGE_BAT_BUOC_MFA: { khoa: KhoaDich; variant: Extract<BadgeVariant, 'primary'> } = {
  khoa: 'nguoiDung.mfa.batBuoc',
  variant: 'primary',
};

/**
 * Dòng phụ "đã cài / chưa cài ứng dụng xác thực" (`mfa_configured`). Bị buộc mà
 * chưa cài tô hổ phách: lần đăng nhập sau người đó bị dừng ở bước cài, quản trị
 * nên biết trước. Không bị buộc thì chưa cài là bình thường, để chữ mờ.
 */
export function trangThaiCaiMfa(
  daCai: boolean | null,
  batBuoc: boolean,
): { khoa: KhoaDich; lop: string } {
  if (daCai === null) return { khoa: 'nguoiDung.mfa.khongRo', lop: 'text-content-muted' };
  if (daCai) return { khoa: 'nguoiDung.mfa.daCai', lop: 'text-content-muted' };
  return { khoa: 'nguoiDung.mfa.chuaCai', lop: batBuoc ? 'text-warning-700' : 'text-content-muted' };
}

/**
 * Nhãn "Tạm khoá đăng nhập" cạnh trạng thái (`login_locked`) — nhóm biến thể
 * **trạng thái**, `warning`: không phải quản trị chủ động khoá (xám) mà là sự
 * việc quản trị nên để mắt — người đó đang không vào được, hoặc ai đó đang dò
 * mật khẩu. Chưa tới mức `danger`: Keycloak tự gỡ sau một thời gian.
 */
export const BADGE_TAM_KHOA_DANG_NHAP: { khoa: KhoaDich; variant: Extract<BadgeVariant, 'warning'> } = {
  khoa: 'nguoiDung.layLai.tamKhoa',
  variant: 'warning',
};

/** Lỗi gửi email đặt lại mật khẩu → câu nói cách xử lý (hiện trong hộp xác nhận). */
export const KHOA_LOI_DAT_LAI_MAT_KHAU: Record<LoiDatLaiMatKhau, KhoaDich> = {
  xungDot: 'nguoiDung.layLai.loiXungDot',
  quaNhieu: 'nguoiDung.layLai.loiQuaNhieu',
  mayChuEmail: 'nguoiDung.layLai.loiMayChuEmail',
};
